import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import { showSuccess } from '@/components/common/Toast';

// ============ Types ============

export type ScopingStage =
  | 'partner_briefing'
  | 'services_positioned'
  | 'scoping_workshop'
  | 'customer_review'
  | 'customer_approval'
  | 'po_sent'
  | 'in_delivery';

export type RagStatus = 'green' | 'amber' | 'red' | 'na';

export interface ProjectScopingStageInfo {
  id: string;
  projectId: string;
  stageId: ScopingStage;
  status: 'not_started' | 'in_progress' | 'completed';
  targetDate: string | null;
  completedDate: string | null;
  confidence: number | null;
  notes: string | null;
}

export interface ParentOpportunity {
  id: string;
  title: string;
  customerName: string;
  servicesValue: number | null;
  unscopedValue: number | null;
  accountDirectorName?: string | null;
  scopingLeadName?: string | null;
  deliveryLeadName?: string | null;
}

export type ProjectStatus =
  | 'draft'
  | 'submitted'
  | 'in_review'
  | 'approved'
  | 'rejected'
  | 'revising'
  | 'archived'
  | 'lost'
  | 'cancelled';

export type LostReason =
  | 'no_budget'
  | 'lost_to_competitor'
  | 'timeline_mismatch'
  | 'scope_mismatch'
  | 'customer_restructure'
  | 'project_cancelled'
  | 'other';

export const LOST_REASONS: Array<{ id: LostReason; label: string }> = [
  { id: 'no_budget', label: 'No Budget' },
  { id: 'lost_to_competitor', label: 'Lost to Competitor' },
  { id: 'timeline_mismatch', label: 'Timeline Mismatch' },
  { id: 'scope_mismatch', label: 'Scope Mismatch' },
  { id: 'customer_restructure', label: 'Customer Restructure' },
  { id: 'project_cancelled', label: 'Project Cancelled' },
  { id: 'other', label: 'Other' },
];

export interface Project {
  id: string;
  name: string;
  customerId: string | null;
  customerName: string;
  description: string | null;
  status: ProjectStatus;
  rateCardId: string | null;
  opportunityId: string | null;
  currentScopingStage: ScopingStage | null;
  fundingMechanism: 'Entitlement' | 'Cash' | 'POC' | null;
  fundingTypeId: string | null;
  fundingTypeName: string | null;
  deliveryLocation: 'Remote' | 'Onsite' | 'Hybrid' | 'Manual' | null;
  currency: 'GBP' | 'USD' | 'EUR' | null;
  exchangeRate: number;
  manualExpenses: number;
  engagementStartDate: string | null;
  daysPerWeek: number;
  architectOversightPct: number;
  pmPct: number;
  bcArchitectOversightPct: number;
  bcPmPct: number;
  totalBaseDays: number;
  totalAdjustedDays: number;
  totalCost: number;
  healthStatus?: RagStatus;
  healthNotes?: string | null;
  scopingLeadId?: string | null;
  scopingLeadName?: string | null;
  deliveryLeadId?: string | null;
  deliveryLeadName?: string | null;
  lostReason?: LostReason | null;
  lostReasonNotes?: string | null;
  lostDate?: string | null;
  lostBy?: string | null;
  deliveryProfileId: string | null;
  version: number;
  ownerId: string;
  createdBy?: string | null;
  updatedBy?: string | null;
  frameworkSnapshot: string | null;
  frameworkSnapshotAt: string | null;
  frameworkSnapshotBy: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectCapability {
  id: string;
  projectId: string;
  capabilityId: string;
  isIncluded: boolean;
  notes: string | null;
}

export type MobilisationStage = 'internal_planning' | 'sales_handover' | 'customer_kickoff' | 'delivery_start';

export interface ProjectMobilisationStageInfo {
  id: string;
  projectId: string;
  stageId: MobilisationStage;
  status: 'not_started' | 'in_progress' | 'completed';
  targetDate: string | null;
  completedDate: string | null;
  notes: string | null;
}

export const PROJECT_MOBILISATION_STAGES: Array<{ id: MobilisationStage; name: string }> = [
  { id: 'internal_planning', name: 'Internal Planning' },
  { id: 'sales_handover', name: 'Sales Handover' },
  { id: 'customer_kickoff', name: 'Customer Kickoff' },
  { id: 'delivery_start', name: 'Delivery Start' },
];

export interface ProjectWithDetails extends Project {
  opportunity: (ParentOpportunity & { scopingLeadName?: string | null; deliveryLeadName?: string | null }) | null;
  scopingStages: ProjectScopingStageInfo[];
  mobilisationStages: ProjectMobilisationStageInfo[];
  capabilities: ProjectCapability[];
  guardrailInputs: Array<{
    id: string;
    guardrailId: string;
    value: string;
    calculatedMultiplier: number;
  }>;
  generalMultipliers: Array<{
    id: string;
    multiplierId: string;
    isEnabled: boolean;
  }>;
}

interface ProjectsResponse {
  projects: Project[];
  total: number;
  limit: number;
  offset: number;
}

interface ProjectResponse {
  project: ProjectWithDetails;
}

interface CreateProjectInput {
  name: string;
  opportunityId: string; // Required - projects are children of opportunities
  description?: string;
  rateCardId?: string;
  deliveryLocation?: 'Remote' | 'Onsite' | 'Hybrid' | 'Manual';
  currency?: 'GBP' | 'USD' | 'EUR';
  engagementStartDate?: string;
  daysPerWeek?: number;
}

interface UpdateProjectInput {
  name?: string;
  description?: string;
  status?: Project['status'];
  rateCardId?: string;
  deliveryLocation?: 'Remote' | 'Onsite' | 'Hybrid' | 'Manual';
  currency?: 'GBP' | 'USD' | 'EUR';
  exchangeRate?: number;
  manualExpenses?: number;
  engagementStartDate?: string;
  daysPerWeek?: number;
  architectOversightPct?: number;
  pmPct?: number;
  bcArchitectOversightPct?: number;
  bcPmPct?: number;
  healthStatus?: RagStatus;
  healthNotes?: string | null;
  scopingLeadId?: string | null;
  scopingLeadName?: string | null;
  deliveryLeadId?: string | null;
  deliveryLeadName?: string | null;
}

// ============ Hooks ============

export function useProjects(params?: {
  status?: Project['status'];
  customerId?: string;
  opportunityId?: string;
  ownerId?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: ['projects', params],
    queryFn: () => api.get<ProjectsResponse>('/projects', params),
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['projects', id],
    queryFn: () => api.get<ProjectResponse>(`/projects/${id}`),
    enabled: !!id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectInput) =>
      api.post<{ project: Project }>('/projects', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: { id: string } & UpdateProjectInput) =>
      api.patch<{ project: Project }>(`/projects/${id}`, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: (_, id) => {
      // Invalidate project list queries but skip the deleted project's detail query.
      // The ProjectDetail page is still mounted at this point and holds an active
      // useProject(id) observer — refetching it would 404 and surface a misleading
      // "Project not found" toast. The observer will unmount when the page navigates
      // away, and the cached entry will be garbage collected.
      queryClient.invalidateQueries({
        queryKey: ['projects'],
        predicate: (query) => query.queryKey[1] !== id,
      });
    },
  });
}

// ============ Project Lifecycle ============

function makeLifecycleHook(action: string, body?: () => Record<string, unknown>) {
  return function useLifecycle() {
    const queryClient = useQueryClient();
    return useMutation({
      mutationFn: (id: string) =>
        api.post<{ success: boolean; status?: string }>(`/projects/${id}/${action}`, body ? body() : {}),
      onSuccess: (_, id) => {
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['projects', id] });
      },
    });
  };
}

export const useSubmitProject = makeLifecycleHook('submit');
export const useApproveProject = makeLifecycleHook('approve');
export const useReviseProject = makeLifecycleHook('revise');
export const useResubmitProject = makeLifecycleHook('resubmit');
export const useCancelRevision = makeLifecycleHook('cancel-revision');
export const useArchiveProject = makeLifecycleHook('archive');
export const useUnlockProject = makeLifecycleHook('unlock');
export const useRebaselineProject = makeLifecycleHook('rebaseline');

// Mark as Lost / Cancelled
export function useMarkProjectLost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      projectId,
      status,
      reason,
      notes,
    }: {
      projectId: string;
      status: 'lost' | 'cancelled';
      reason: LostReason;
      notes?: string | null;
    }) => api.post<{ project: Project }>(`/projects/${projectId}/mark-lost`, {
      status,
      reason,
      notes: notes || null,
    }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
}

// Reopen lost/cancelled project back to draft
export function useReopenProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (projectId: string) =>
      api.post<{ project: Project }>(`/projects/${projectId}/reopen`, {}),
    onSuccess: (_, projectId) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
}

export function useRejectProject() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      api.post<{ success: boolean }>(`/projects/${id}/reject`, { reason }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', id] });
    },
  });
}

export function useSetProjectCapabilities() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      capabilities,
    }: {
      projectId: string;
      capabilities: Array<{ capabilityId: string; isIncluded: boolean; notes?: string }>;
    }) => api.put(`/projects/${projectId}/capabilities`, { capabilities }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] }); // Refresh opportunity totals
    },
  });
}

export function useSetProjectGuardrails() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      guardrails,
    }: {
      projectId: string;
      guardrails: Array<{ guardrailId: string; value: string }>;
    }) => api.put(`/projects/${projectId}/guardrails`, { guardrails }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useSetProjectMultipliers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      multipliers,
    }: {
      projectId: string;
      multipliers: Array<{ multiplierId: string; isEnabled: boolean }>;
    }) => api.put(`/projects/${projectId}/general-multipliers`, { multipliers }),
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ['projects', projectId] });
    },
  });
}

export function useExportProjectPptx() {
  return useMutation({
    mutationFn: ({ projectId, projectName }: { projectId: string; projectName: string }) => {
      const filename = `${projectName.replace(/[^a-z0-9]/gi, '_')}_Scoping.pptx`;
      return api.downloadFile(`/projects/${projectId}/export/pptx`, filename);
    },
  });
}

// Update project scoping stage
export function useUpdateProjectScopingStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      stageId,
      ...data
    }: {
      projectId: string;
      stageId: ScopingStage;
      status: 'not_started' | 'in_progress' | 'completed';
      targetDate?: string | null;
      completedDate?: string | null;
      confidence?: number | null;
      notes?: string | null;
    }) =>
      api.put<{ stage: ProjectScopingStageInfo }>(
        `/projects/${projectId}/scoping-stages/${stageId}`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['opportunities'] });
    },
  });
}

export function useUpdateProjectMobilisationStage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      stageId,
      ...data
    }: {
      projectId: string;
      stageId: MobilisationStage;
      status: 'not_started' | 'in_progress' | 'completed';
      targetDate?: string | null;
      completedDate?: string | null;
      notes?: string | null;
    }) =>
      api.put<{ stage: ProjectMobilisationStageInfo }>(
        `/projects/${projectId}/mobilisation-stages/${stageId}`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
    },
  });
}

// Scoping stage metadata for UI
export const PROJECT_SCOPING_STAGES: Array<{ id: ScopingStage; name: string; color: string }> = [
  { id: 'partner_briefing', name: 'Partner Briefing', color: 'bg-gray-100' },
  { id: 'services_positioned', name: 'Services Positioned', color: 'bg-blue-100' },
  { id: 'scoping_workshop', name: 'Scoping Workshop', color: 'bg-yellow-100' },
  { id: 'customer_review', name: 'Customer Review', color: 'bg-orange-100' },
  { id: 'customer_approval', name: 'Customer Approval', color: 'bg-purple-100' },
  { id: 'po_sent', name: 'PO Sent', color: 'bg-green-100' },
  { id: 'in_delivery', name: 'In Delivery', color: 'bg-emerald-100' },
];

// ============ Delivery Plan ============

export interface DeliveryAllocation {
  activityId: string;
  week: number;
  days: number;
}

export interface ActivityOverride {
  activityId: string;
  overrideDays: number | null;
  overrideRole: string | null;
}

export interface DeliveryPlan {
  allocations: DeliveryAllocation[];
  capabilityStartWeeks: Record<string, number>;
  activityOverrides: ActivityOverride[];
}

export interface ProjectVersion {
  id: string;
  version: number;
  changeDescription: string | null;
  changedFields: string[] | null;
  createdBy: string | null;
  createdByEmail: string | null;
  createdAt: string;
}

// Get delivery plan
export function useDeliveryPlan(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'delivery-plan'],
    queryFn: () => api.get<DeliveryPlan>(`/projects/${projectId}/delivery-plan`),
    enabled: !!projectId,
  });
}

// Save delivery plan
export function useSaveDeliveryPlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      allocations,
      capabilityStartWeeks,
      changeDescription,
    }: {
      projectId: string;
      allocations: Array<{
        activityId: string;
        weekAllocations: Record<string, number>;
        baseDaysOverride?: number;
        roleOverride?: string;
      }>;
      capabilityStartWeeks: Record<string, number>;
      changeDescription?: string;
    }) =>
      api.put<{ success: boolean; version: number; savedAt: string }>(
        `/projects/${projectId}/delivery-plan`,
        { allocations, capabilityStartWeeks, changeDescription }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'delivery-plan'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'versions'] });
      showSuccess('Delivery plan saved');
    },
  });
}

// Get version history
export function useProjectVersions(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'versions'],
    queryFn: () => api.get<{ versions: ProjectVersion[] }>(`/projects/${projectId}/versions`),
    enabled: !!projectId,
  });
}

// Restore version
export function useRestoreProjectVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      versionId,
    }: {
      projectId: string;
      versionId: string;
    }) =>
      api.post<{ success: boolean; newVersion: number; restoredFromVersion: number }>(
        `/projects/${projectId}/versions/${versionId}/restore`,
        {}
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'delivery-plan'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'versions'] });
    },
  });
}

// ============ Pricing Calculation ============

export interface RoleBreakdown {
  days: number;
  cost: number;
}

export interface WorkstreamBreakdown {
  id: string;
  name: string;
  days: number;
  cost: number;
}

export interface AppliedMultiplier {
  id: string;
  name: string;
  factor: number;
}

export interface PricingOutput {
  totalBaseDays: number;
  totalAdjustedDays: number;
  oversightDays: number;
  pmDays: number;
  totalDays: number;
  totalCost: number;
  byRole: Record<string, RoleBreakdown>;
  byWorkstream: WorkstreamBreakdown[];
  appliedMultipliers: AppliedMultiplier[];
}

/**
 * Calculate pricing for a project using backend calculation
 */
export function useProjectPricing(projectId: string) {
  return useQuery({
    queryKey: ['projects', projectId, 'pricing'],
    queryFn: () => api.get<{ pricing: PricingOutput }>(`/projects/${projectId}/calculate-pricing`),
    enabled: !!projectId,
    staleTime: 30 * 1000, // 30 seconds - pricing should be fairly fresh
  });
}

// ============ Available Delivery Profiles ============

export interface AvailableProfile {
  id: string;
  name: string;
  description: string | null;
}

export function useAvailableDeliveryProfiles() {
  return useQuery({
    queryKey: ['delivery-profiles', 'available'],
    queryFn: () => api.get<{ profiles: AvailableProfile[] }>('/vcf/delivery-profiles'),
    staleTime: 5 * 60 * 1000,
  });
}

// ============ Effective Delivery Plan ============

/**
 * Effective delivery plan = Services Framework baseline merged with an
 * optional Service Package overlay. Used by projects to resolve what
 * base days and weekly allocations to inherit when the project itself
 * has no override in place.
 *
 * The backend endpoint is GET /vcf/delivery-plan/effective?profileId=...
 * and takes per-activity override semantics: if the package has any
 * rows for an activity, the package owns that activity's schedule;
 * otherwise framework wins.
 *
 * Days are returned in CENTDAYS (matching storage).
 */
export interface EffectiveDeliveryPlan {
  allocations: Array<{ activityId: string; week: number; days: number }>;
  activities: Array<{ activityId: string; baseDays: number }>;
  capabilityStartWeeks: Array<{ capabilityId: string; startWeek: number }>;
}

export function useEffectiveDeliveryPlan(profileId: string | null | undefined) {
  return useQuery({
    queryKey: ['effective-delivery-plan', profileId ?? null],
    queryFn: () => {
      const qs = profileId ? `?profileId=${encodeURIComponent(profileId)}` : '';
      return api.get<EffectiveDeliveryPlan>(`/vcf/delivery-plan/effective${qs}`);
    },
    staleTime: 30 * 1000,
  });
}

// ============ Apply Delivery Profile ============

interface ApplyProfileParams {
  projectId: string;
  profileId: string;
  applyCapabilities: boolean;
  applyGuardrails: boolean;
  applyAllocations: boolean;
}

interface ApplyProfileResponse {
  success: boolean;
  version: number;
  appliedProfile: { id: string; name: string };
  appliedCapabilities: number;
  appliedGuardrails: number;
  appliedAllocations: number;
}

export function useApplyDeliveryProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ projectId, ...body }: ApplyProfileParams) =>
      api.post<ApplyProfileResponse>(`/projects/${projectId}/apply-profile`, body),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'delivery-plan'] });
      queryClient.invalidateQueries({ queryKey: ['projects', variables.projectId, 'versions'] });
      showSuccess(`Applied profile: ${data.appliedProfile.name}`);
    },
  });
}
